import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { List, X, CaretDown, Phone } from '@phosphor-icons/react';
import { Link } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';
import LanguageDropdown from '@/components/LanguageDropdown';
import LogoDisplay from '@/components/LogoDisplay';
import { CONTACT_PHONE, CONTACT_PHONE_DISPLAY } from '@/config';

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isGammeOpen, setIsGammeOpen] = useState(false);
  const { language, t } = useLanguage();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = isMobileMenuOpen ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [isMobileMenuOpen]);

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
    setIsGammeOpen(false);
  };

  const navLinks = [
    { key: 'accueil', href: '#accueil' },
    { key: 'services', href: '#services' },
    { key: 'apropos', href: '#apropos' },
    { key: 'contact', href: '#contact' },
  ];

  const GAMME_ITEMS = [
    { key: 'comfortClassique', href: '#gammes' },
    { key: 'comfortPremium', href: '#gammes' },
    { key: 'prestige', href: '#gammes' },
    { key: 'van', href: '#gammes' },
  ];
  const gammeMenuId = 'navbar-gamme-menu';
  const gammeButtonId = 'navbar-gamme-button';

  return (
    <motion.header
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.6, ease: 'easeOut' }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-[#0A0A0A]/92 backdrop-blur-xl border-b border-[#D4AF37]/10' : 'bg-transparent'
      }`}
      data-testid="navbar"
    >
      <div className="border-b border-white/5 bg-[#050505]/88">
        <div className="landing-shell hidden items-center justify-between py-2.5 text-[0.7rem] uppercase tracking-[0.32em] text-[#C7B588] md:flex lg:py-3">
          <span>Service chauffeur privé premium</span>
          <div className="flex items-center gap-6">
            <a href={`tel:${CONTACT_PHONE}`} className="inline-flex items-center gap-2 transition-colors duration-300 hover:text-[#F3D67A]">
              <Phone size={12} weight="fill" className="text-[#D4AF37]" />
              {CONTACT_PHONE_DISPLAY}
            </a>
          </div>
        </div>
      </div>

      <nav className="landing-shell flex flex-wrap items-center justify-between gap-x-4 gap-y-4 py-4 sm:gap-x-5 sm:gap-y-5 sm:py-5 md:py-6 lg:gap-y-5 xl:grid xl:grid-cols-[auto,minmax(0,1fr),auto] xl:items-center xl:gap-6 xl:py-4 2xl:gap-7 2xl:py-4">
        <a href="#accueil" className="flex shrink-0 items-center self-center" data-testid="logo">
          <span className="rounded-[1.75rem] border border-[#D4AF37]/20 bg-[#0E0E0E]/92 px-3 py-2.5 shadow-[0_18px_48px_rgba(0,0,0,0.3)] sm:px-4 sm:py-3 lg:px-4 lg:py-3 xl:px-4 xl:py-2.5">
            <LogoDisplay className="h-[2rem] w-[7.5rem] sm:h-[2.5rem] sm:w-[9.5rem] md:h-[2.75rem] md:w-[10.5rem] lg:h-[3rem] lg:w-[11.375rem] xl:h-[2.75rem] xl:w-[10.5rem] 2xl:h-[2.875rem] 2xl:w-[11rem]" priority />
          </span>
        </a>

        <div className="hidden min-w-0 items-center justify-center lg:order-3 lg:flex lg:basis-full xl:order-none xl:basis-auto xl:flex-1">
          <div className="flex w-full min-w-0 items-center justify-center gap-3 rounded-[2rem] border border-white/8 bg-[#111111]/88 px-4 py-3 shadow-[0_18px_50px_rgba(0,0,0,0.28)] xl:mx-auto xl:max-w-[34rem] xl:gap-4 xl:px-5 xl:py-2.5 2xl:max-w-[36rem] 2xl:gap-5 2xl:px-6">
            {navLinks.map((link) => (
              <a
                key={link.key}
                href={link.href}
                className={`${
                  link.key === 'contact' ? 'text-[#D4AF37]' : 'text-[#CFCFCF]'
                } whitespace-nowrap text-[0.78rem] uppercase tracking-[0.18em] transition-colors duration-300 hover:text-[#D4AF37] xl:text-[0.88rem] 2xl:text-[0.92rem]`}
                data-testid={`nav-link-${link.key}`}
              >
                {t(link.key)}
              </a>
            ))}

            <div
              className="relative"
              onMouseEnter={() => setIsGammeOpen(true)}
              onMouseLeave={() => setIsGammeOpen(false)}
              onBlur={(event) => {
                const menuRoot = event.currentTarget;
                window.setTimeout(() => {
                  if (!menuRoot.contains(document.activeElement)) {
                    setIsGammeOpen(false);
                  }
                }, 0);
              }}
            >
              <button
                id={gammeButtonId}
                type="button"
                onClick={() => setIsGammeOpen((prev) => !prev)}
                onKeyDown={(event) => {
                  if (event.key === 'Escape') {
                    event.preventDefault();
                    event.stopPropagation();
                    setIsGammeOpen(false);
                    return;
                  }
                  if (event.key === 'ArrowDown' || event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    setIsGammeOpen(true);
                  }
                }}
                aria-expanded={isGammeOpen}
                aria-controls={isGammeOpen ? gammeMenuId : undefined}
                className="flex items-center gap-1 whitespace-nowrap text-[0.78rem] uppercase tracking-[0.18em] text-[#CFCFCF] transition-colors duration-300 hover:text-[#D4AF37] xl:text-[0.88rem] 2xl:text-[0.92rem]"
                data-testid="nav-link-gamme"
              >
                {t('gamme')} <CaretDown size={14} className={`transition-transform duration-200 ${isGammeOpen ? 'rotate-180' : ''}`} />
              </button>

              <AnimatePresence>
                {isGammeOpen && (
                  <motion.div
                    id={gammeMenuId}
                    aria-labelledby={gammeButtonId}
                    initial={{ opacity: 0, y: -8 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -8 }}
                    transition={{ duration: 0.18 }}
                    className="absolute top-full left-0 mt-3 w-52 overflow-hidden rounded-2xl shadow-xl"
                    style={{ background: '#111111', border: '1px solid rgba(212,175,55,0.18)' }}
                  >
                    {GAMME_ITEMS.map((item) => (
                      <a
                        key={item.key}
                        href={item.href}
                        className="block px-4 py-3 text-sm text-[#D4AF37] transition-colors duration-200 hover:bg-[#1A1A1A]"
                        onClick={() => setIsGammeOpen(false)}
                      >
                        {t(item.key)}
                      </a>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
        </div>

        <div className="hidden shrink-0 items-center gap-2 self-center lg:flex xl:gap-2.5 2xl:gap-3">
          <LanguageDropdown
            buttonClassName="rounded-full px-3.5 py-2 text-[0.88rem] xl:px-4 xl:py-2.5"
            menuClassName="mt-3"
          />

          <a
            href="#reserver"
            className="rounded-full px-4 py-2.5 text-[0.88rem] font-semibold transition-all duration-300 hover:scale-105 xl:px-5 xl:py-2.5 xl:text-[0.95rem] 2xl:px-6 2xl:py-3 2xl:text-[1rem]"
            style={{ background: '#D4AF37', color: '#0A0A0A' }}
            data-testid="cta-reserver"
          >
            {t('reserver')}
          </a>

          <Link
            to={`/${language}/login`}
            className="rounded-full border border-[#D4AF37]/70 px-4 py-2.5 text-[0.88rem] font-semibold text-[#D4AF37] transition-all duration-300 hover:bg-[#D4AF37] hover:text-[#232323] xl:px-5 xl:py-2.5 xl:text-[0.95rem] 2xl:px-6 2xl:py-3 2xl:text-[1rem]"
            data-testid="btn-connexion"
          >
            {t('connexion')}
          </Link>
        </div>

        <div className="flex items-center gap-3 lg:hidden">
          <button
            className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-white/10 bg-[#111111]/90 text-[#FAFAFA] transition-colors hover:text-[#D4AF37]"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            data-testid="mobile-menu-toggle"
            aria-label={isMobileMenuOpen ? 'Fermer le menu' : 'Ouvrir le menu'}
          >
            {isMobileMenuOpen ? <X size={28} /> : <List size={28} />}
          </button>
        </div>
      </nav>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="lg:hidden border-t border-[#D4AF37]/10 bg-[#090909]/98 backdrop-blur-xl"
            data-testid="mobile-menu"
          >
            <div className="flex max-h-[calc(100dvh-5rem)] flex-col gap-4 overflow-y-auto px-4 pb-[calc(1rem+env(safe-area-inset-bottom))] pt-4 sm:px-6 sm:pt-6">
              <div className="rounded-2xl border border-[#D4AF37]/20 bg-[#121212] p-4">
                <div className="mb-3 flex items-center justify-between gap-3">
                  <LogoDisplay className="h-[34px] w-[132px]" priority />
                  <div className="shrink-0">
                    <LanguageDropdown />
                  </div>
                </div>
                <a
                  href={`tel:${CONTACT_PHONE}`}
                  className="inline-flex items-center gap-2 text-sm font-medium text-[#F3D67A]"
                  onClick={closeMobileMenu}
                >
                  <Phone size={15} weight="fill" />
                  {CONTACT_PHONE_DISPLAY}
                </a>
              </div>

              {navLinks.map((link) => (
                <a
                  key={link.key}
                  href={link.href}
                  className={`${
                    link.key === 'contact' ? 'text-[#D4AF37]' : 'text-[#A1A1AA]'
                  } hover:text-[#D4AF37] transition-colors py-2 text-lg break-words`}
                  onClick={closeMobileMenu}
                >
                  {t(link.key)}
                </a>
              ))}

              {/* Gamme sub-items in mobile */}
              <div>
                <p className="text-[#A1A1AA] text-sm uppercase tracking-wide py-1">{t('gamme')}</p>
                <div className="pl-4 flex flex-col gap-2 mt-1">
                  {GAMME_ITEMS.map((item) => (
                    <a
                      key={item.key}
                      href={item.href}
                      className="py-1 text-base transition-colors duration-200 text-[#D4AF37]"
                      onClick={closeMobileMenu}
                    >
                      {t(item.key)}
                    </a>
                  ))}
                </div>
              </div>

              <a
                href="#reserver"
                className="font-semibold w-full text-center py-3 rounded-lg transition-all duration-300 mt-2"
                style={{ background: '#D4AF37', color: '#0A0A0A' }}
                onClick={closeMobileMenu}
              >
                {t('reserver')}
              </a>

              {/* Connexion */}
              <Link
                to={`/${language}/login`}
                className="font-semibold w-full text-center py-3 rounded-lg transition-all duration-300"
                style={{ border: '1.5px solid #D4AF37', color: '#D4AF37', background: 'transparent' }}
                onClick={closeMobileMenu}
              >
                {t('connexion')}
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};

export default Navbar;
